import { Box } from "arkh";
import React, { Suspense } from "react";

import { TodoToolbarContainer } from "widgets";

import {
  TodoToolbarItem,
  ToolbarFeature,
  TodoToolbarItemFeature,
} from "widgets/src/Todo/TodoToolbar/TodoToolbarItem";
import { ToolbarButtonAssignees } from "widgets/src/Todo/TodoToolbar/ToolbarButtonAssignees";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { useTodoCreateState } from "./TodoCreateState";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { TodoCreateToolbar_data$key } from "./__generated__/TodoCreateToolbar_data.graphql";
import { TodoCreateToolbarAssigneesQuery } from "./__generated__/TodoCreateToolbarAssigneesQuery.graphql";
import { TodoToolbarButtonTags_tags$key } from "widgets/src/Todo/TodoToolbar/__generated__/TodoToolbarButtonTags_tags.graphql";
import { notEmpty } from "@/src/utils";
import { useFormattedDateRaw } from "@/src/common/dates/useFormattedDateRaw";

function sortOrdinal(a: "hidden" | "active" | "inactive") {
  switch (a) {
    case "hidden":
      return 0;
    case "active":
      return 2;
    case "inactive":
      return 3;
  }
}

type SortableItem =
  | {
      type: "feature";
      feature: TodoToolbarItemFeature;
      mode: "active" | "inactive";
    }
  | { type: "assignee"; mode: "active" | "inactive" };

export const TodoCreateToolbar = React.memo(TodoCreateToolbarC);
function TodoCreateToolbarC(props: {
  openFeature: (feature: ToolbarFeature) => void;
  hasTags: boolean;
  data: TodoCreateToolbar_data$key;
  allTags: readonly (TodoToolbarButtonTags_tags$key[number] & { id: string })[];
}) {
  const { state } = useTodoCreateState();
  const data = useFragment(
    graphql`
      fragment TodoCreateToolbar_data on Query {
        teamsByIds(ids: $teamIds) {
          ...TodoToolbarButtonTeams_teams
        }
      }
    `,
    props.data
  );

  const formattedDeadline = useFormattedDateRaw(
    state.expireAt?.toISOString() ?? undefined,
    "DEFAULT"
  );

  const items = ((): SortableItem[] => {
    const all: (SortableItem | { type: "hidden" })[] = [
      {
        type: "feature" as const,
        feature: {
          feature: "TEAM" as const,
          teams: data.teamsByIds?.filter(notEmpty) ?? [],
        },
        mode: "active" as const,
      },
      {
        type: "feature" as const,
        feature: {
          feature: "PRIORITY" as const,
          priority: state.priority ?? "DEFAULT",
        },
        mode:
          state.priority && state.priority !== "DEFAULT"
            ? ("active" as const)
            : ("inactive" as const),
      },
      {
        type: "feature" as const,
        feature: {
          feature: "DEADLINE" as const,
          expireAt:
            state.expireAt && formattedDeadline
              ? {
                  date: state.expireAt,
                  formattedDate: formattedDeadline,
                }
              : undefined,
        },
        mode: state.expireAt ? ("active" as const) : ("inactive" as const),
      },
      {
        type: "assignee" as const,
        mode:
          state.employeeIds.length > 0 || state.roleIds.length > 0
            ? ("active" as const)
            : ("inactive" as const),
      },
      props.hasTags
        ? {
            type: "feature" as const,
            feature: {
              feature: "TAG" as const,
              tags: props.allTags.filter((t) => state.tagIds.includes(t.id)),
            },
            mode:
              state.tagIds.length > 0
                ? ("active" as const)
                : ("inactive" as const),
          }
        : { type: "hidden" as const },
    ];
    return (all.filter((f) => f.type !== "hidden") as SortableItem[]).sort(
      (a, b) => sortOrdinal(a.mode) - sortOrdinal(b.mode)
    );
  })();

  const { currentEmployeeId } = usePersistedEmployeeId();

  const onPressAssignee = () => {
    props.openFeature("ASSIGNEE");
  };

  return (
    <Box space="medium" spaceTop="none">
      <TodoToolbarContainer
        editable={
          items.length > 0 ? (
            <>
              {items.map((item) =>
                item.type === "assignee" ? (
                  <Suspense key="ASSIGNEE" fallback={null}>
                    <TodoCreateToolbarAssignees
                      onPress={onPressAssignee}
                      currentEmployeeId={currentEmployeeId ?? undefined}
                    />
                  </Suspense>
                ) : (
                  <TodoToolbarItem
                    key={item.feature.feature}
                    feature={item.feature}
                    onPress={props.openFeature}
                    mode={item.mode}
                    isReadOnly={false}
                    currentEmployeeId={currentEmployeeId ?? undefined}
                  />
                )
              )}
            </>
          ) : null
        }
      />
    </Box>
  );
}

function TodoCreateToolbarAssignees(props: {
  onPress: () => void;
  currentEmployeeId?: string;
}) {
  const { state } = useTodoCreateState();
  const data = useLazyLoadQuery<TodoCreateToolbarAssigneesQuery>(
    graphql`
      query TodoCreateToolbarAssigneesQuery(
        $employeeIds: [ID!]!
        $roleIds: [ID!]!
      ) {
        employeesByIds(ids: $employeeIds) {
          ...ToolbarButtonAssignees_employees
        }
        rolesByIds(ids: $roleIds) {
          ...ToolbarButtonAssignees_roles
        }
      }
    `,
    { employeeIds: state.employeeIds, roleIds: state.roleIds },
    { fetchPolicy: "store-and-network" }
  );

  return (
    <ToolbarButtonAssignees
      onPress={props.onPress}
      assignedEmployees={data.employeesByIds?.filter(notEmpty) ?? []}
      assignedRoles={data.rolesByIds?.filter(notEmpty) ?? []}
      isLocked={false}
    />
  );
}
